import { jsx as _jsx } from "react/jsx-runtime";
/**
 * How the centre viewport shows the scene (ADR-0006). `'3D'` mounts the R3F
 * canvas, `'2D'` the Control overlay. The default (3D, collisions off, labels
 * on, gizmos on, grid off, fog on, performance off) needs no provider, and the host layers
 * persistence on top.
 */
import { createContext, useContext, useMemo, useState } from 'react';
/**
 * The localStorage keys of the persisted viewport preferences. The shell
 * reads them once. `<ViewportToolbar>` writes them on a user choice, and a
 * programmatic change, a per-scene derivation, never persists.
 */
export const VIEWPORT_MODE_STORAGE_KEY = 'tsi.viewportMode';
export const SHOW_GRID_STORAGE_KEY = 'tsi.showGrid';
export const SHOW_GIZMOS_STORAGE_KEY = 'tsi.showGizmos';
/**
 * Whether to frame the scene on load. Off by default, as Godot's editor opens
 * at a fixed orbit. A framed scene also puts every object in the frustum, so
 * nothing is culled.
 */
export const FRAME_ON_OPEN_STORAGE_KEY = 'tsi.frameOnOpen';
/** Whether to draw the fog. A user who hides it for speed keeps it hidden on the next open. */
export const SHOW_FOG_STORAGE_KEY = 'tsi.showFog';
export const SHOW_PERFORMANCE_STORAGE_KEY = 'tsi.showPerformance';
/** The preferences of a session that persisted none: 3D, gizmos on, no grid, Godot's fixed orbit, fog on. */
export const DEFAULT_VIEWPORT = {
    mode: '3D',
    showGizmos: true,
    showGrid: false,
    frameOnOpen: false,
    showFog: true,
    showPerformance: false,
};
const DEFAULT_VALUE = {
    ...DEFAULT_VIEWPORT,
    setMode: () => { },
    showCollisions: false,
    setShowCollisions: () => { },
    showLabels: true,
    setShowLabels: () => { },
    showNavigation: true,
    setShowNavigation: () => { },
    setShowGizmos: () => { },
    setShowGrid: () => { },
    showPreviewSun: true,
    setShowPreviewSun: () => { },
    showPreviewEnvironment: true,
    setShowPreviewEnvironment: () => { },
    setFrameOnOpen: () => { },
    setShowFog: () => { },
    setShowPerformance: () => { },
};
const ViewportModeContext = createContext(DEFAULT_VALUE);
ViewportModeContext.displayName = 'ViewportModeContext';
/**
 * The Gizmos toggle on its own. Every node with a gizmo reads it, and a node that read the whole
 * viewport value would re-render on each grid, fog or label change.
 */
const ShowGizmosContext = createContext(DEFAULT_VIEWPORT.showGizmos);
ShowGizmosContext.displayName = 'ShowGizmosContext';
export function ViewportModeProvider({ children, initialMode = DEFAULT_VIEWPORT.mode, initialShowCollisions = false, initialShowLabels = true, initialShowNavigation = true, initialShowGizmos = DEFAULT_VIEWPORT.showGizmos, initialShowGrid = DEFAULT_VIEWPORT.showGrid, initialShowPreviewSun = true, initialShowPreviewEnvironment = true, initialFrameOnOpen = DEFAULT_VIEWPORT.frameOnOpen, initialShowFog = DEFAULT_VIEWPORT.showFog, initialShowPerformance = DEFAULT_VIEWPORT.showPerformance, }) {
    const [mode, setMode] = useState(initialMode);
    const [showCollisions, setShowCollisions] = useState(initialShowCollisions);
    const [showLabels, setShowLabels] = useState(initialShowLabels);
    const [showNavigation, setShowNavigation] = useState(initialShowNavigation);
    const [showGizmos, setShowGizmos] = useState(initialShowGizmos);
    const [showGrid, setShowGrid] = useState(initialShowGrid);
    const [showPreviewSun, setShowPreviewSun] = useState(initialShowPreviewSun);
    const [showPreviewEnvironment, setShowPreviewEnvironment] = useState(initialShowPreviewEnvironment);
    const [frameOnOpen, setFrameOnOpen] = useState(initialFrameOnOpen);
    const [showFog, setShowFog] = useState(initialShowFog);
    const [showPerformance, setShowPerformance] = useState(initialShowPerformance);
    const value = useMemo(() => ({
        mode,
        setMode,
        showCollisions,
        setShowCollisions,
        showLabels,
        setShowLabels,
        showNavigation,
        setShowNavigation,
        showGizmos,
        setShowGizmos,
        showGrid,
        setShowGrid,
        showPreviewSun,
        setShowPreviewSun,
        showPreviewEnvironment,
        setShowPreviewEnvironment,
        frameOnOpen,
        setFrameOnOpen,
        showFog,
        setShowFog,
        showPerformance,
        setShowPerformance,
    }), [
        mode,
        showCollisions,
        showLabels,
        showNavigation,
        showGizmos,
        showGrid,
        showPreviewSun,
        showPreviewEnvironment,
        frameOnOpen,
        showFog,
        showPerformance,
    ]);
    return (_jsx(ViewportModeContext.Provider, { value: value, children: _jsx(ShowGizmosContext.Provider, { value: showGizmos, children: children }) }));
}
/** The viewport mode and the overlay toggles. Safe without a provider. */
export function useViewportMode() {
    return useContext(ViewportModeContext);
}
/**
 * Whether the editor gizmos draw. Every other toggle reads `useViewportMode()`. This one has a context of
 * its own, so a gizmo re-renders for the Gizmos toggle alone.
 */
export function useShowGizmos() {
    return useContext(ShowGizmosContext);
}
//# sourceMappingURL=ViewportModeContext.js.map